<template>
    <div class="about-us-view">
        <!-- ─── Hero banner (breadcrumb + copy + image, matches design) ───── -->
        <section class="au-hero-banner cmn-section p-0">
            <div class="au-hero-banner__container">
                <div class="au-hero-banner__grid">
                    <div class="au-hero-banner__left">
                        <nav class="au-hero-breadcrumb" aria-label="breadcrumb">
                            <ol class="au-hero-breadcrumb__list">
                                <li class="au-hero-breadcrumb__item">
                                    <router-link class="au-hero-breadcrumb__link" to="/">Home</router-link>
                                </li>
                                <li class="au-hero-breadcrumb__sep" aria-hidden="true">»</li>
                                <li class="au-hero-breadcrumb__item au-hero-breadcrumb__item--current"
                                    aria-current="page">
                                    About Us
                                </li>
                            </ol>
                        </nav>
                        <h1 class="au-banner-heading">{{ main.banner_heading || "About Us" }}</h1>
                        <p class="au-banner-sub">{{ main.banner_subtext || "We're about you and everything you love!" }}
                        </p>
                        <div class="au-hero-banner__cta-row">
                            <a :href="main.banner_cta_link ? getExternalUrl(main.banner_cta_link) : '/'"
                                class="btn au-cta-btn" target="_blank" rel="noopener noreferrer">
                                {{ main.banner_cta_text || "Shop Now" }}
                            </a>
                            <!-- Decorative badge cluster (design only) -->
                        </div>
                    </div>
                    <div class="au-hero-banner__right">
                        <img class="au-hero-banner__img" :src="main.banner_image || '/maincss/images/about-us-img.png'"
                            alt="about-us-banner" />

                    </div>
                </div>
            </div>
        </section>

        <!-- ─── Our Story ───────────────────────────────────── -->
        <section class="au-section au-story-section">
            <div class="container">
                <div class="row au-story-row">
                    <!-- Left: story image -->
                    <div class="col-md-5 left_story_image" v-if="main.story_left_image">
                        <img :src="main.story_left_image" alt="our-story" class="au-story-img" />
                    </div>
                    <!-- Right: story text -->
                    <div :class="main.story_left_image && 'right_story_text' ? 'col-md-7' : 'col-md-12' " v-if="main.story_description">
                        <div class="au-story-text" :class="{ 'au-story-text--full': !main.story_left_image }">
                            <h2 class="au-story-title">Our Story is Your Story</h2>
                            <p v-if="main.story_subtitle" class="au-story-subtitle">{{ main.story_subtitle }}</p>
                            <p v-if="main.story_description" class="au-story-desc">{{ storyDescriptionPreview
                                }}<template v-if="storyDescriptionExceedsLimit">...</template></p>
                            <button v-if="storyDescriptionExceedsLimit" class="au-read-more au-read-more--btn"
                                @click="storyModalOpen = true">Read More ›</button>
                        </div>
                    </div>
                </div>

            </div>
        </section>

        <!-- ─── Story bottom banner (full bleed) ────────────── -->
        <section class="p-0">
            <div class="au-hero-bottom-banner__container">
                <div v-if="main.story_bottom_banner_image" class="au-bottom-banner">
                    <img :src="main.story_bottom_banner_image" alt="story-bottom-banner" class="au-full-img" />
                </div>
            </div>

        </section>


        <!-- ─── Our Promise ─────────────────────────────────── -->
        <section class="au-section au-promise-section"
            v-if="promiseGallery.length || main.promise_description || promiseBullets.length || featureHighlights.length">
            <div class="container">
                <h2 class="au-section-heading au-section-heading--lined">Our Promise to You</h2>

                <div class="row" v-if="promiseGallery.length || main.promise_description || promiseBullets.length">
                    <!-- Left: gallery slider -->
                    <div class="col-md-5" v-if="promiseGallery.length">
                        <div class="au-slider" @touchstart.passive="onSliderTouchStart"
                            @touchend.passive="onSliderTouchEnd" @touchcancel.passive="onSliderTouchCancel">
                            <img :src="promiseGallery[currentSlideIndex].image_path" alt="promise-gallery"
                                class="au-slider-img" />
                            <template v-if="promiseGallery.length > 1">
                                <!-- <button class="au-arrow au-arrow-left" @click="prevSlide"
                                    aria-label="Previous">&#8249;</button>
                                <button class="au-arrow au-arrow-right" @click="nextSlide"
                                    aria-label="Next">&#8250;</button> -->
                                <div class="au-dots">
                                    <button v-for="dot in visiblePromiseDots" :key="'promise_dot_' + dot.index"
                                        class="au-dot" :class="{ active: currentSlideIndex === dot.index }"
                                        @click="goToSlide(dot.index)"></button>
                                </div>
                            </template>
                        </div>
                        <div class="au-view-gallery-wrap">
                            <button class="au-view-gallery-btn" @click="galleryModalOpen = true">
                                View Gallery <span class="au-vg-arrow"><svg xmlns="http://www.w3.org/2000/svg" width="8"
                                        height="14" viewBox="0 0 8 14" fill="none">
                                        <path d="M1 1L7 7L1 13" stroke="#1D6CB5" stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                    </svg></span>
                            </button>
                        </div>
                    </div>
                    <!-- Right: promise text + bullets -->
                    <div :class="promiseGallery.length ? 'col-md-7' : 'col-md-12'">
                        <div class="au-promise-text" :class="{ 'pl-0': !promiseGallery.length }">
                            <p v-if="main.promise_subtitle" class="au-promise-subtitle">{{ main.promise_subtitle }}</p>
                            <p v-if="main.promise_description" class="au-promise-desc">{{ main.promise_description }}
                            </p>
                            <div v-if="promiseBullets.length" class="au-bullets-wrap">
                                <ul class="au-bullets">
                                    <li v-for="b in promiseBullets.slice(0, Math.ceil(promiseBullets.length / 2))"
                                        :key="'pb_l_'+b.id">{{ b.bullet_text }}</li>
                                </ul>
                                <ul class="au-bullets" v-if="promiseBullets.length > 1">
                                    <li v-for="b in promiseBullets.slice(Math.ceil(promiseBullets.length / 2))"
                                        :key="'pb_r_'+b.id">{{ b.bullet_text }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Feature highlight cards -->
                <div class="row au-feature-row" v-if="featureHighlights.length">
                    <div class="col-md-3 col-sm-6 mb-4" v-for="f in featureHighlights" :key="'fh_'+f.id">
                        <div class="au-feature-card">
                            <div class="au-feature-bar"></div>
                            <h5>{{ f.title }}</h5>
                            <p>{{ f.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ─── 100% Satisfaction Guarantee ────────────────── -->
        <section class="au-satisfaction-section" v-if="main.satisfaction_title || main.satisfaction_description">
            <div class="container">
                <div class="au-satisfaction-row">
                    <div class="au-satisfaction-left">
                        <h2 class="au-satisfaction-title">{{ main.satisfaction_title || "100% satisfaction guarantee" }}
                        </h2>
                    </div>
                    <div class="au-satisfaction-right">
                        <p class="au-satisfaction-desc">{{ main.satisfaction_description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ─── Meet Our Team ───────────────────────────────── -->
        <section class="au-section team_section" v-if="teamMembers.length">
            <div class="container">
                <h2 class="au-section-heading">Meet Our Team</h2>
                <div class="row justify-content-center">
                    <div class="col-md-3 col-sm-6 mb-4 team_column" v-for="t in teamMembers" :key="'tm_'+t.id">
                        <div class="au-team-card">
                            <div class="au-team-img-wrap">
                                <img v-if="t.image" :src="t.image" :alt="t.name" class="au-team-img" />
                                <div v-else class="au-team-img-placeholder"></div>
                            </div>
                            <div class="au-team-info">
                                <p class="au-team-name">{{ t.name }}</p>
                                <p class="au-team-designation">{{ t.designation }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ─── Why Imprint City ────────────────────────────── -->
        <section class="au-section au-why-section" v-if="whyItems.length">
            <div class="container">
                <h2 class="au-section-heading">Why Imprint City is the perfect partner for you</h2>
                <div class="au-why-grid">
                    <div class="au-why-card" v-for="w in whyItems" :key="'wi_'+w.id">
                        <img v-if="w.icon_image" :src="w.icon_image" :alt="w.title" class="au-why-icon" />
                        <div v-else class="au-why-icon-placeholder"></div>
                        <div class="au-why-text">
                            <h5 class="au-why-title">{{ w.title }}</h5>
                            <p class="au-why-desc">{{ w.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <TrustedBySection :logos="trustedByLogos" section-class="no-bg" />

        <!-- ─── Story Read More Modal ───────────────────────── -->
        <ContentReadMoreModal :visible="storyModalOpen" :image="main.story_left_image || ''"
            title="Our Story is Your Story" :subtitle="main.story_subtitle || ''"
            :description="main.story_description || ''" @close="storyModalOpen = false" />

        <!-- ─── Gallery Modal ───────────────────────────────── -->
        <ImageGalleryModal :visible="galleryModalOpen" :images="promiseGallery" image-key="image_path"
            title="Our Promise Gallery" @close="galleryModalOpen = false" />

        <TestimonialsSection :testimonials="testimonials" />

        <!-- ─── Join Our Team ──────────────────────────────── -->
        <section class="au-join-team"
            v-if="main.join_team_subtitle || main.join_team_button_text || main.join_team_button_link">
            <div class="container text-center">
                <h2 class="au-join-title">Join Our Team</h2>
                <p class="au-join-subtitle">{{ main.join_team_subtitle }}</p>
                <a :href="main.join_team_button_link ? getExternalUrl(main.join_team_button_link) : '#'"
                    class="btn au-join-btn" target="_blank" rel="noopener noreferrer">
                    {{ main.join_team_button_text || 'APPLY NOW' }}
                </a>
            </div>
        </section>

    </div>
</template>

<script>
    import ApiService from "@/common/api.service";
    import TrustedBySection from "@/components/frontend/TrustedBySection.vue";
    import TestimonialsSection from "@/components/frontend/TestimonialsSection.vue";
    import ImageGalleryModal from "@/components/frontend/ImageGalleryModal.vue";
    import ContentReadMoreModal from "@/components/frontend/ContentReadMoreModal.vue";

    export default {
        name: "AboutUs",
        components: {
            TrustedBySection,
            TestimonialsSection,
            ImageGalleryModal,
            ContentReadMoreModal
        },
        data() {
            return {
                main: {},
                promiseBullets: [],
                promiseGallery: [],
                currentSlideIndex: 0,
                sliderTouchStartX: null,
                featureHighlights: [],
                teamMembers: [],
                whyItems: [],
                trustedByLogos: [],
                testimonials: [],
                galleryModalOpen: false,
                storyModalOpen: false
            };
        },
        mounted() {
            document.body.classList.remove("bg_primary_light_white");
            this.fetchAboutCms();
            this.fetchTestimonials();
            this.fetchTrustedByLogos();
        },
        computed: {
            storyWordCount() {
                const text = (this.main && this.main.story_description) || "";
                return text.trim().split(/\s+/).filter(Boolean).length;
            },
            storyDescriptionExceedsLimit() {
                return this.storyWordCount > 200;
            },
            storyDescriptionPreview() {
                const text = (this.main && this.main.story_description) || "";
                if (!this.storyDescriptionExceedsLimit) return text;
                // Walk character by character — count words but preserve original whitespace/newlines
                let wordCount = 0;
                let i = 0;
                while (i < text.length && wordCount < 200) {
                    while (i < text.length && /\s/.test(text[i])) i++;
                    if (i >= text.length) break;
                    while (i < text.length && !/\s/.test(text[i])) i++;
                    wordCount++;
                }
                return text.slice(0, i);
            },
            visiblePromiseDots() {
                const total = this.promiseGallery.length;
                if (total <= 3) {
                    return this.promiseGallery.map((img, index) => ({ img, index }));
                }

                const prevIndex = (this.currentSlideIndex - 1 + total) % total;
                const nextIndex = (this.currentSlideIndex + 1) % total;
                return [prevIndex, this.currentSlideIndex, nextIndex].map((index) => ({
                    img: this.promiseGallery[index],
                    index
                }));
            }
        },
        methods: {
            async fetchAboutCms() {
                try {
                    const res = await ApiService.query("about-us-cms", { params: {} });
                    const d = res.data || {};
                    this.main = d.about_us || {};
                    this.promiseBullets = d.promise_bullets || [];
                    this.promiseGallery = d.promise_gallery || [];
                    this.currentSlideIndex = 0;
                    this.featureHighlights = d.feature_highlights || [];
                    this.teamMembers = d.team_members || [];
                    this.whyItems = d.why_items || [];
                    this.trustedByLogos = d.trusted_by_logos || [];
                } catch (e) {
                    console.log("About CMS load failed", e);
                }
            },
            async fetchTestimonials() {
                try {
                    const res = await ApiService.get("/home-testimonials");
                    this.testimonials = (res.data && res.data.testimonials) || [];
                } catch (e) {
                    console.log("Testimonials load failed", e);
                }
            },
            async fetchTrustedByLogos() {
                try {
                    const res = await ApiService.query("trusted-by-logos", { params: {} });
                    this.trustedByLogos = (res.data && res.data.trusted_by_logos) || [];
                } catch (e) {
                    console.log("Trusted logos load failed", e);
                }
            },
            prevSlide() {
                this.currentSlideIndex = this.currentSlideIndex === 0 ? this.promiseGallery.length - 1 : this.currentSlideIndex - 1;
            },
            nextSlide() {
                this.currentSlideIndex = this.currentSlideIndex === this.promiseGallery.length - 1 ? 0 : this.currentSlideIndex + 1;
            },
            goToSlide(i) {
                this.currentSlideIndex = i;
            },
            onSliderTouchStart(event) {
                if (!event.touches || !event.touches.length) return;
                this.sliderTouchStartX = event.touches[0].clientX;
            },
            onSliderTouchEnd(event) {
                if (this.sliderTouchStartX === null) return;
                if (!event.changedTouches || !event.changedTouches.length) {
                    this.sliderTouchStartX = null;
                    return;
                }

                const touchEndX = event.changedTouches[0].clientX;
                const deltaX = touchEndX - this.sliderTouchStartX;
                const swipeThreshold = 40;

                if (Math.abs(deltaX) >= swipeThreshold && this.promiseGallery.length > 1) {
                    if (deltaX > 0) {
                        this.prevSlide();
                    } else {
                        this.nextSlide();
                    }
                }

                this.sliderTouchStartX = null;
            },
            onSliderTouchCancel() {
                this.sliderTouchStartX = null;
            },
            getExternalUrl(link) {
                const raw = (link || "").trim();
                if (!raw) return "#";
                if (/^https?:\/\//i.test(raw)) return raw;
                return `https://${raw}`;
            }
        }
    };
</script>

<style scoped>
    /* Belt-and-braces with App `.app-main` for vw/full-bleed breakout in this route */
    .about-us-view {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
    }

    /* ─── Shared section ─────────────────────────────── */
    .au-section {
        padding: 48px 0;
    }

    .team_section {
        background-color: #E8F0F8;
        padding: 64px 40px;
        margin-top: 64px;
    }

    .au-section-heading {
        font-size: 40px;
        font-weight: 500;
        color: #000;
        margin-bottom: 40px;
        text-align: center;
    }

    .au-section-heading--lined {
        display: flex;
        align-items: center;
        gap: 16px;
        white-space: nowrap;
    }

    .au-section-heading--lined::before,
    .au-section-heading--lined::after {
        content: "";
        flex: 1;
        height: 1px;
        background: #B6B6B6;
    }

    /* ─── Hero banner + breadcrumb (About Us hero) ─── */
    .au-hero-banner {
        background: linear-gradient(117.86deg, #EDEDE8 24.47%, #F1F3F1 59.2%);
        min-height: 380px;
        margin-top: 35px;
    }

    .au-hero-banner__container {
        position: relative;
    }

    .au-hero-banner__grid {
        display: flex;
        align-items: stretch;
        justify-content: space-between;
        gap: 0 24px;
        min-height: 380px;
    }

    .au-hero-banner__left {
        flex: 1 1 50%;
        max-width: 560px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 10px 0 48px 80px;
        z-index: 2;
    }

    .au-hero-breadcrumb {
        margin: 0 0 100px;
        padding: 0;
    }

    .au-hero-breadcrumb__list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0 6px;
        font-family: "proximaNova-regular", sans-serif;
        font-size: 13px;
        line-height: 1.35;
    }

    .au-hero-breadcrumb__link {
        color: #337ab7 !important;
        text-decoration: none !important;
    }

    .au-hero-breadcrumb__link:hover {
        color: #23527c !important;
        text-decoration: underline !important;
    }

    .au-hero-breadcrumb__sep {
        color: #337ab7;
    }

    .au-hero-breadcrumb__item--current {
        color: #6c757d;
    }

    .au-banner-heading {
        font-size: 48px;
        font-weight: 700;
        color: #000;
        line-height: 50px;
        margin: 0 0 16px;
        letter-spacing: -0.5px;
    }

    .au-banner-sub {
        font-family: "proximaNova-regular", Helvetica, Arial, sans-serif;
        font-size: 16px;
        font-weight: 400;
        color: #606060;
        margin: 0 0 24px;
        line-height: 20px;
    }

    .au-hero-banner__cta-row {
        position: relative;
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;
        gap: 16px 24px;
    }

    .au-cta-btn {
        background: #f38118;
        color: #fff !important;
        border: none;
        padding: 12px 8px;
        border-radius: 2px;
        font-family: "proxima Nova", sans-serif;
        font-size: 14px;
        line-height: 16px;
        font-weight: 600;
        display: inline-block;
        text-decoration: none;
        box-shadow: none;
        max-width: 121px;
        height: 40px;
        min-width: 121px;
    }

    .au-cta-btn:hover {
        background: #d97010;
        color: #fff !important;
        text-decoration: none;
    }

    /* Right column: photo */
    .au-hero-banner__right {
        flex: 1 1 45%;
        position: relative;
        min-height: 360px;
        align-self: stretch;
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;
    }

    .au-hero-banner__img {
        width: 100%;
        max-width: 854px;
        min-width: 854px;
        height: 100%;
        min-height: 380px;
        max-height: 380px;
        object-fit: cover;
        object-position: right center;
        display: block;
        margin-left: auto;
    }

    /* Floating decorative avatars */
    .au-hero-floating-badge--bs {
        margin-left: 8px;
        margin-bottom: 4px;
    }

    .au-hero-floating-badge__pill {
        display: inline-flex;
        align-items: center;
        background: #fff;
        border-radius: 40px;
        padding: 10px 18px 10px 12px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
    }

    .au-hero-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        font-size: 15px;
        font-weight: 700;
        line-height: 1;
        border: 3px solid #fff;
        box-sizing: content-box;
    }

    .au-hero-avatar--b {
        background: #f5c518;
        color: #111;
        z-index: 2;
    }

    .au-hero-avatar--s {
        background: #4a6278;
        color: #fff;
        margin-left: -14px;
        z-index: 1;
    }

    .au-hero-floating-badge--r {
        position: absolute;
        right: -8px;
        top: 38%;
        transform: translateY(-50%);
        z-index: 3;
    }

    .au-hero-floating-badge--r__bubble {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border-radius: 12px;
        padding: 12px 14px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
        position: relative;
    }

    .au-hero-floating-badge--r__bubble::after {
        content: "";
        position: absolute;
        right: -6px;
        top: 50%;
        transform: translateY(-50%);
        border: 7px solid transparent;
        border-left-color: #fff;
        filter: drop-shadow(2px 0 1px rgba(0, 0, 0, 0.04));
    }

    .au-hero-avatar--r {
        width: 38px;
        height: 38px;
        background: #2e8b57;
        color: #fff;
        border: none;
        font-size: 16px;
    }



    /* ─── Our Story (two-column comp) ───────────────── */
    .au-story-section {
        background: #fff;
        padding-top: 56px;
        padding-bottom: 56px;
    }

    .au-story-row {
        margin-left: -10px;
        margin-right: -10px;
    }

    .au-story-row>[class*="col-"] {
        padding-left: 32px;
        padding-right: 32px;
    }

    .au-story-section .left_story_image {
        padding-left: 11px;
    }

    .au-story-section .right_story_text {
        padding-right: 11px;
    }

    .au-story-img {
        width: 100%;
        height: auto;
        max-height: 560px;
        object-fit: cover;
        object-position: center top;
        display: block;
    }



    .au-story-text--full {
        padding-left: 0;
        max-width: none;
    }

    .au-story-title {
        font-size: 40px;
        line-height: 40px;
        font-weight: 700;
        color: #000;
        margin: 0 0 24px;
        letter-spacing: -0.02em;
    }

    .au-story-subtitle {
        font-family: "proximaNova-regular", Helvetica, Arial, sans-serif;
        font-size: 16px;
        font-weight: 700;
        font-style: italic;
        color: #0056b3;
        margin: 0 0 24px;
        line-height: 24px;
    }

    .au-story-desc {
        font-family: "proximaNova-regular", Helvetica, Arial, sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        color: #414141;
        white-space: pre-line;
        margin: 0 0 22px;
    }

    .au-story-desc:last-child {
        margin-bottom: 0;
    }

    .au-story-section .au-read-more,
    .au-story-section .au-read-more--btn {
        display: inline-block;
        margin-top: 8px;
        color: #000;
        font-weight: 700;
        font-size: 15px;
        text-decoration: none;
    }

    .au-story-section .au-read-more:hover,
    .au-story-section .au-read-more--btn:hover {
        color: #000;
        text-decoration: none;
    }

    .au-read-more--btn {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-family: inherit;
    }





    /* ─── Bottom banner ──────────────────────────────── */
    .au-bottom-banner {
        /* margin-left: calc(-50vw + 50%);
        margin-right: calc(-50vw + 50%); */
    }

    .au-full-img {
        width: 100%;
        height: 560px;
        object-fit: cover;
        display: block;
    }

    /* ─── Promise section ────────────────────────────── */
    .au-promise-section {
        background: #f9f9f9;
    }

    .au-promise-text {
        padding-left: 28px;
    }

    .au-promise-subtitle {
        font-family: "proximaNova-regular", sans-serif;
        font-size: 16px;
        line-height: 24px;
        font-weight: 700;
        font-style: italic;
        color: #1D6CB5;
        margin: 0 0 20px;
    }

    .au-promise-desc {
        font-size: 16px;
        line-height: 24px;
        color: #414141;
        white-space: pre-line;
        margin-bottom: 14px;
    }

    .au-bullets-wrap {
        display: flex;
        gap: 8px;
    }

    .au-bullets-wrap .au-bullets {
        flex: 1;
    }

    .au-bullets {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .au-bullets li {
        padding: 1px 0 1px 18px;
        position: relative;
        font-family: "proximaNova-regular", sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.2px;
        color: #414141;
    }

    .au-bullets li::before {
        content: "•";
        position: absolute;
        left: 0;
        color: #414141;
        font-size: 16px;
        line-height: 24px;
    }

    /* ─── View Gallery button ────────────────────────── */
    .au-view-gallery-wrap {
        margin-top: 12px;
        text-align: center;
    }

    .au-view-gallery-btn {
        background: none;
        border: none;
        padding: 0;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
        color: #1D6CB5;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    .au-view-gallery-btn svg {
        width: 6px;
        height: 12px;
    }

    .au-view-gallery-btn:hover {
        text-decoration: underline;
    }

    .au-vg-arrow {
        font-size: 18px;
        line-height: 1;
    }

    /* ─── Gallery slider ─────────────────────────────── */
    .au-slider {
        position: relative;
        border-radius: 0px;
        overflow: hidden;
    }

    .au-slider-img {
        width: 100%;
        height: 435px;
        object-fit: cover;
        display: block;
    }

    .au-arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: #fff;
        font-size: 26px;
        line-height: 36px;
        text-align: center;
        cursor: pointer;
        padding: 0;
    }

    .au-arrow-left {
        left: 10px;
    }

    .au-arrow-right {
        right: 10px;
    }

    .au-dots {
        display: flex;
        justify-content: center;
        gap: 7px;
        padding: 8px 0 4px;
        background: transparent;
        bottom: 24px;
        position: absolute;
        width: 100%;
    }

    .au-dot {
        width: 12px;
        height: 12px;
        border: none;
        border-radius: 50%;
        background: #929292;
        padding: 0;
        cursor: pointer;
    }

    .au-dot.active {
        background: #F2790C;
    }

    /* ─── Feature highlight cards ────────────────────── */
    .au-feature-card {
        height: 100%;
        padding-top: 0px;
    }

    .au-feature-bar {
        width: 100%;
        height: 3px;
        background: #F2790C;
        margin-bottom: 24px;
    }

    .au-feature-card h5 {
        font-size: 18px;
        line-height: 18px;
        font-weight: 700;
        margin-bottom: 12px;
        color: #000000;
    }

    .au-feature-card p {
        font-size: 14px;
        line-height: 18px;
        color: #606060;
        font-weight: 400;
    }

    /* ─── Satisfaction ───────────────────────────────── */
    .au-satisfaction-section {
        background: #F2790C1A;
        padding: 40px;
    }

    .au-satisfaction-row {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }

    .au-satisfaction-left {
        flex: 0 0 50%;
    }

    .au-satisfaction-title {
        font-family: 'Fraunces', serif;
        font-size: 40px;
        font-weight: 400;
        font-style: italic;
        color: #411F00;
        line-height: 24px;
        letter-spacing: -0.2px;
        margin: 0;
    }

    .au-satisfaction-right {
        flex: 1;
    }

    .au-satisfaction-desc {
        font-size: 18px;
        line-height: 30px;
        color: #411F00;
        font-weight: 500;
        margin: 0;
    }

    .team_column {
        padding: 0 8px;
    }

    /* ─── Team cards ─────────────────────────────────── */
    .au-team-card {
        text-align: center;
    }

    .au-team-img-wrap {
        width: 100%;
        aspect-ratio: 328 / 288;
        overflow: hidden;
        background: #e8e8e8;
        margin-bottom: 20px;
    }

    .au-team-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
        display: block;
    }

    .au-team-img-placeholder {
        width: 100%;
        height: 100%;
        background: #e0e0e0;
    }

    .au-team-info {
        padding: 0 2px;
    }

    .au-team-name {
        font-weight: 700;
        font-size: 18px;
        line-height: 18px;
        margin: 0 0 12px;
        color: #000000;
    }

    .au-team-designation {
        font-size: 14px;
        line-height: 18px;
        font-weight: 400;
        color: #606060;
        margin: 0;
    }

    /* ─── Why section ────────────────────────────────── */
    .au-why-section {
        background: #fff;
        padding: 64px 40px;
    }

    .au-why-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 36px 48px;
    }

    .au-why-card {
        display: flex;
        align-items: flex-start;
        gap: 28px;
    }

    .au-why-icon {
        width: 40px;
        height: 40px;
        object-fit: contain;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .au-why-icon-placeholder {
        width: 44px;
        height: 44px;
        flex-shrink: 0;
    }

    .au-why-title {
        font-size: 18px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #000000;
    }

    .au-why-desc {
        font-size: 14px;
        line-height: 24px;
        font-weight: 400;
        color: #606060;
        margin: 0;
    }



    /* ─── Join Our Team ─────────────────────────────── */
    .au-join-team {
        background: #1D6CB5;
        padding: 64px 40px;
    }

    .au-join-title {
        color: #fff;
        font-size: 40px;
        line-height: 40px;
        font-weight: 500;
        margin: 0 0 24px;
    }

    .au-join-subtitle {
        color: #FFFFFF;
        font-size: 16px;
        line-height: 16px;
        font-weight: 400;
        margin: 0 0 16px;
    }

    .au-join-btn {
        max-width: 140px;
        background: #fff;
        color: #2b6fb3 !important;
        border: none;
        border-radius: 3px;
        font-size: 16px;
        line-height: 20px;
        font-weight: 400;
        padding: 8px;
        height: 44px;
        min-width: 140px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
    }

    .au-join-btn:hover {
        background: #f3f3f3;
        color: #2b6fb3 !important;
        text-decoration: none;
    }

    .au-feature-row {
        margin-top: 30px;
    }

    @media (min-width: 1921px) {
        .au-hero-bottom-banner__container {
            max-width: 1920px;
            margin: 0 auto;
            padding: 0px 70px;
        }
        .au-hero-banner__container {
            padding: 0px 74px;
        }
    }

    @media (min-width: 1600px) {
        .au-hero-banner__container {
            max-width: 1920px;
            margin: 0 auto;
            padding: 0px 40px;
        }
    }

    @media (max-width: 1250px) {
        .au-hero-banner__left {
            padding-left: 40px;
        }

        .au-hero-banner__img {
            min-width: 700px;
        }
    }

    @media (max-width: 991px) {
        .au-hero-breadcrumb {
            margin-bottom: 40px;
        }

        .au-hero-banner__grid {
            flex-direction: column;
            min-height: unset;
        }

        .au-hero-banner__left {
            max-width: none;
            padding: 28px 0 0;
            order: 1;
            padding-bottom: 20px;
            padding-left: 20px;
        }

        .au-promise-text {
            padding-left: 0px;
            padding-top: 20px;
        }

        .au-hero-banner__right {
            order: 0;
            min-height: 280px;
            justify-content: center;
            margin-left: calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            width: 100vw;
            max-width: none;
        }

        .au-hero-banner__img {
            max-width: none;
            min-height: 280px;
            object-position: center top;
        }

        .au-hero-floating-badge--r {
            right: 12px;
            top: 50%;
        }

        .au-satisfaction-title {
            font-size: 32px;
            line-height: 32px;
        }
    }

    @media (max-width: 575px) {
        .au-hero-banner__cta-row {
            flex-direction: column;
            align-items: flex-start;
        }

        .au-hero-floating-badge--bs {
            margin-left: 0;
        }
    }

    @media (max-width: 767px) {
        .au-story-section {
            padding-top: 40px;
            padding-bottom: 44px;
        }

        .au-story-row {
            margin-left: -15px;
            margin-right: -15px;
        }

        .au-story-row>[class*="col-"] {
            padding-left: 15px;
            padding-right: 15px;
        }

        .au-story-text {
            padding-left: 0;
            margin-top: 24px;
        }

        .au-story-img {
            max-height: 360px;
        }

        .au-why-grid {
            grid-template-columns: 1fr;
            gap: 24px;
        }

        .au-satisfaction-row {
            flex-direction: column;
            gap: 20px;
        }

        .au-satisfaction-left {
            flex: unset;
        }

        .au-dots {
            bottom: 50px;
        }
    }
</style>